import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DEVELOPER_URL } from '@/config';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore, useIsSuperAdmin } from '../../store/useAuthStore';
import { post } from '../../api/api.client';
import { API_ENDPOINTS } from '../../api/endpoints';
import { ConfirmModal } from '../../components/ConfirmModal';
import { MaintenancePage } from '../../components/MaintenancePage';
import { ScrollManager } from '../../components/ScrollManager';
import { AdminSEO } from '../../components/SEO';
import { usePublicSettings } from '@/hooks';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { usePermissions } from '../../hooks/usePermissions';
import { useUnreadCount, useNotifications, useMarkAsRead, useMarkAllAsRead } from '../../hooks/useNotifications';
import { useOnboarding } from '../../hooks/useOnboarding';
import { AdminTopBar } from './AdminTopBar';
import { AdminSidebar } from './AdminSidebar';
import { buildSidebarGroups, filterSidebarGroups, type SidebarGroup } from './sidebarConfig';

export function AdminLayout() {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const isSuperAdmin = useIsSuperAdmin();
  const { data: publicSettings } = usePublicSettings();
  const { canAccess } = usePermissions();
  const queryClient = useQueryClient();

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { data: unreadData } = useUnreadCount();
  const { data: notificationsData } = useNotifications(1, 10);
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const { startTour, isCompleted, isActive: tourActive } = useOnboarding();

  useEffect(() => {
    if (location.pathname === '/admin' && !isCompleted('dashboard')) {
      const timer = setTimeout(() => startTour('dashboard'), 800);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isCompleted, startTour]);

  const unreadCount = unreadData?.count ?? 0;
  const notificationsList = (notificationsData?.data ?? []).map((n) => ({
    id: n.id,
    type: (n.type as 'member' | 'donation' | 'contact' | 'event' | 'system') || 'system',
    title: n.title,
    message: n.message,
    createdAt: n.createdAt,
    read: n.isRead,
  }));

  const { data: stats } = useDashboardStats();

  const badgeCounts = {
    pendingMembers: stats?.members?.pending ?? 0,
    unreadContacts: 0,
  };

  const sidebarGroups: SidebarGroup[] = buildSidebarGroups(t);
  const filteredGroups = filterSidebarGroups(sidebarGroups, isSuperAdmin, canAccess);

  const isActive = (path: string) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await post(API_ENDPOINTS.AUTH.LOGOUT, {});
    } catch {
      // Continue with local logout even if API call fails
    }
    queryClient.clear();
    logout();
    navigate('/login');
  };

  if (publicSettings?.KILLSWITCH_ADMIN === 'true' && !isSuperAdmin) {
    return (
      <MaintenancePage
        type="maintenance"
        onRetry={() => {
          queryClient.clear();
          logout();
          navigate('/login');
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSEO />

      <AdminTopBar
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        user={user}
        unreadCount={unreadCount}
        notificationsList={notificationsList}
        notificationsOpen={notificationsOpen}
        setNotificationsOpen={setNotificationsOpen}
        markAsRead={markAsRead}
        markAllAsRead={markAllAsRead}
      />

      <AdminSidebar
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        filteredGroups={filteredGroups}
        isActive={isActive}
        user={user}
        badgeCounts={badgeCounts}
        onLogout={handleLogoutClick}
        startTour={startTour}
        tourActive={tourActive}
      />

      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        title={t('admin.logout.confirmTitle')}
        message={t('admin.logout.confirmMessage')}
        confirmLabel={t('admin.logout.confirm')}
        cancelLabel={t('admin.logout.cancel')}
        variant="warning"
      />

      <main
        className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}
      >
        <div data-tour="main-content" className="p-4 lg:p-6 min-h-[calc(100vh-8rem)]">
          <ScrollManager enableTransitions={false}>
            <Outlet />
          </ScrollManager>
        </div>

        <footer className="px-4 lg:px-6 py-4 border-t border-slate-100 bg-white">
          <p className="text-center text-sm text-slate-600">
            {t('public.footer.poweredBy')}{' '}
            <a
              href={DEVELOPER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1E40AF] hover:text-blue-700 transition-colors font-medium"
            >
              Agent
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}

export default AdminLayout;
